import { ref } from 'vue'
import { FEED_CONSTANTS, NAVIGATION_DIRECTION } from '@/Shared/feedConstants'

/**
 * Composable for handling touch drag navigation
 * Provides real-time visual feedback and threshold-based navigation
 *
 * @param {Function} navigateToPost - Navigation handler function
 * @param {Ref} scrollLocked - Reference to scroll lock state
 * @param {Ref} containerHeight - Reference to container height
 * @returns {Object} Touch handlers and state
 */
export function useTouchNavigation(navigateToPost, scrollLocked, containerHeight) {
    const touchStartY = ref(0)
    const touchCurrentY = ref(0)
    const isDragging = ref(false)
    const dragDelta = ref(0)

    const resetTouchState = () => {
        isDragging.value = false
        touchStartY.value = 0
        touchCurrentY.value = 0
        dragDelta.value = 0
    }

    const handleTouchStart = (event) => {
        if (scrollLocked.value) return

        touchStartY.value = event.touches[0].clientY
        touchCurrentY.value = touchStartY.value
        isDragging.value = true
    }

    const handleTouchMove = (event) => {
        if (!isDragging.value || scrollLocked.value) return

        touchCurrentY.value = event.touches[0].clientY
        dragDelta.value = touchCurrentY.value - touchStartY.value

        event.preventDefault()
    }

    const handleTouchEnd = () => {
        if (!isDragging.value || scrollLocked.value) return

        const threshold = containerHeight.value * FEED_CONSTANTS.DRAG_THRESHOLD
        const shouldNavigate = Math.abs(dragDelta.value) > threshold

        if (shouldNavigate) {
            const direction = dragDelta.value < 0 ? NAVIGATION_DIRECTION.NEXT : NAVIGATION_DIRECTION.PREV

            isDragging.value = false
            touchStartY.value = 0
            touchCurrentY.value = 0
            dragDelta.value = 0

            navigateToPost(direction)
        } else {
            isDragging.value = false
            touchStartY.value = 0
            touchCurrentY.value = 0

            requestAnimationFrame(() => {
                dragDelta.value = 0
            })
        }
    }

    return {
        isDragging,
        dragDelta,
        handleTouchStart,
        handleTouchMove,
        handleTouchEnd,
        resetTouchState,
    }
}
